import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Briefcase, MapPin, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { InnerBlock, GridCards } from "@/components/InnerBlock";
import { CheckCircle2, AlertCircle, Upload } from "lucide-react";

const PERKS = [
  { title: "Direct client contact from month one", desc: "" },
  { title: "A modern stack with no legacy prison", desc: "" },
  { title: "AI tooling as standard", desc: "" },
  { title: "Flexible hours across time zones", desc: "" },
  { title: "Real ownership of a module, not a task", desc: "" },
];

const inputCls = "h-11 w-full rounded-md border border-wafa-deep/15 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-wafa-primary";
const labelCls = "block text-xs font-semibold uppercase tracking-wider text-wafa-deep";

export default function CareersPage() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/jobs").then(({ data }) => setJobs(data.items || []))
      .catch(() => setJobs([]))
      .finally(() => setLoading(false));
  }, []);

  // Group by department when there are more than 6.
  const grouped = jobs.length > 6
    ? jobs.reduce((acc, j) => { (acc[j.department] ||= []).push(j); return acc; }, {})
    : null;

  return (
    <div data-testid="careers-page">
      <Seo title="Careers at Wafa Solutions" description="Join a small senior team building and running real products. Roles in engineering, design and delivery." />
      <PageHero
        eyebrow="Careers"
        h1="Build things that run in the real world."
        breadcrumbs={[{ label: "Home", to: "/" }, { label: "Company", to: "/company" }, { label: "Careers" }]}
      />

      <InnerBlock tint="white" heading="Small team. Real products. No layers.">
        <p className="max-w-3xl text-base leading-relaxed text-wafa-ink/85 lg:text-lg">
          Our engineers talk to clients directly, make architecture decisions, and see their code carrying real orders within a week of writing it. If you want to be one of forty people on a ticket queue, this is the wrong place.
        </p>
      </InnerBlock>

      <InnerBlock tint="cream" heading="What you get">
        <GridCards items={PERKS} columns={3} />
        <p className="mt-6 text-sm text-wafa-ink/75">
          We hire and train Omani nationals, and we are happy to discuss our current position openly.
        </p>
      </InnerBlock>

      <InnerBlock tint="white" heading="Open roles">
        {loading ? (
          <div className="text-sm text-wafa-ink/60" data-testid="careers-loading">Loading…</div>
        ) : jobs.length === 0 ? (
          <>
            <div className="rounded-lg border border-wafa-deep/10 bg-wafa-cream/40 p-6" data-testid="careers-empty">
              <p className="text-sm text-wafa-ink/85 lg:text-base">
                <span className="font-semibold text-wafa-deep">Nothing open right now</span> — send us something you have built anyway. We keep good CVs.
              </p>
            </div>
            <OpenCVForm />
          </>
        ) : grouped ? (
          <div className="space-y-10" data-testid="careers-jobs-grouped">
            {Object.entries(grouped).map(([dept, list]) => (
              <div key={dept}>
                <h3 className="font-heading text-xl font-semibold text-wafa-deep">{dept}</h3>
                <div className="mt-4 grid gap-4 md:grid-cols-2">
                  {list.map((j) => <JobCard key={j.id} job={j} />)}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2" data-testid="careers-jobs-flat">
            {jobs.map((j) => <JobCard key={j.id} job={j} />)}
          </div>
        )}
      </InnerBlock>
    </div>
  );
}

function JobCard({ job }) {
  return (
    <Link
      to={`/company/careers/${job.slug}`}
      data-testid={`job-card-${job.slug}`}
      className="group flex flex-col rounded-lg border border-wafa-deep/10 bg-white p-6 transition-all hover:-translate-y-0.5 hover:border-wafa-deep/25 hover:shadow-[0_18px_40px_-20px_rgba(14,79,58,0.25)]"
    >
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-wafa-primary">
        <Briefcase className="h-3.5 w-3.5" /> {job.department}
      </div>
      <div className="mt-3 font-heading text-xl font-semibold text-wafa-deep sm:text-2xl">{job.title}</div>
      <p className="mt-3 text-sm text-wafa-ink/75 lg:text-base">{job.summary}</p>
      <div className="mt-6 flex flex-wrap items-center gap-3 text-xs text-wafa-ink/70">
        <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{job.location}</span>
        <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{job.employment_type}</span>
        <span className="ms-auto inline-flex items-center gap-1 font-semibold text-wafa-primary">
          View role <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
        </span>
      </div>
    </Link>
  );
}

function OpenCVForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", role_of_interest: "", portfolio_url: "", cv_name: "", message: "" });
  const [state, setState] = useState({ stage: "idle", error: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) { setState({ stage: "error", error: "Please enter your name and a valid email." }); return; }
    setState({ stage: "sending", error: "" });
    try { await api.post("/careers", form); setState({ stage: "sent", error: "" }); }
    catch (_) { setState({ stage: "error", error: "Something went wrong. Please try again." }); }
  };
  if (state.stage === "sent") return (
    <div className="mt-10 rounded-lg border border-wafa-primary/30 bg-wafa-cream/40 p-8 text-center" data-testid="careers-success">
      <CheckCircle2 className="mx-auto h-10 w-10 text-wafa-primary" />
      <h3 className="mt-3 font-heading text-xl font-semibold text-wafa-deep">Thanks — we have your CV.</h3>
      <p className="mt-2 text-sm text-wafa-ink/75">We&apos;ll be in touch when something fits.</p>
    </div>
  );
  return (
    <form onSubmit={submit} data-testid="careers-form" className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2">
      <div><label className={labelCls}>Name *</label><input data-testid="careers-name" className={inputCls + " mt-2"} value={form.name} onChange={set("name")} /></div>
      <div><label className={labelCls}>Email *</label><input type="email" data-testid="careers-email" className={inputCls + " mt-2"} value={form.email} onChange={set("email")} /></div>
      <div><label className={labelCls}>Phone</label><input data-testid="careers-phone" className={inputCls + " mt-2"} value={form.phone} onChange={set("phone")} /></div>
      <div><label className={labelCls}>Role of interest</label><input data-testid="careers-role" className={inputCls + " mt-2"} value={form.role_of_interest} onChange={set("role_of_interest")} /></div>
      <div className="sm:col-span-2"><label className={labelCls}>Portfolio / GitHub link</label><input data-testid="careers-portfolio" className={inputCls + " mt-2"} value={form.portfolio_url} onChange={set("portfolio_url")} /></div>
      <div className="sm:col-span-2">
        <label className={labelCls}>CV</label>
        <div className="mt-2 flex items-center gap-3">
          <input type="file" id="c-cv" data-testid="careers-cv" onChange={(e) => setForm({ ...form, cv_name: e.target.files?.[0]?.name || "" })} className="hidden" />
          <label htmlFor="c-cv" className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-wafa-deep/15 bg-white px-4 py-2 text-sm font-medium text-wafa-deep hover:border-wafa-deep"><Upload className="h-4 w-4" /> Choose file</label>
          {form.cv_name ? <span className="text-xs text-wafa-ink/70">{form.cv_name}</span> : null}
        </div>
      </div>
      <div className="sm:col-span-2"><label className={labelCls}>Message</label><textarea data-testid="careers-message" rows={4} className="mt-2 w-full rounded-md border border-wafa-deep/15 bg-white p-3 text-sm focus:outline-none focus:ring-2 focus:ring-wafa-primary" value={form.message} onChange={set("message")} /></div>
      {state.stage === "error" ? <div className="sm:col-span-2 flex items-center gap-2 text-sm text-wafa-tomato" data-testid="careers-error"><AlertCircle className="h-4 w-4" /> {state.error}</div> : null}
      <div className="sm:col-span-2">
        <button type="submit" disabled={state.stage === "sending"} data-testid="careers-submit" className="inline-flex items-center justify-center gap-2 rounded-md bg-wafa-primary px-6 py-3 text-sm font-semibold text-white hover:bg-wafa-deep disabled:opacity-60">
          {state.stage === "sending" ? "Sending…" : "Send your CV"}
        </button>
      </div>
    </form>
  );
}
